import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton, TextField } from '@fly/core';
import { TableFilter, TableFilterOption } from '@fly/shared';
import { FlySvgSprite } from '@fly/svg';
import { Separator } from '@radix-ui/themes';
import classNames from 'classnames';
import cn from 'classnames';

import { FlyFilterDetailsDropdown } from './FlyFilterDetailsDropdown';
import { FlyFilterDropdown } from './FlyFilterDropdown';

export type FlyTableControlsProps<T extends object> = {
  search: string;
  setSearch: (search: string) => void;
  fullSearchWidth?: boolean;
  possibleFilters?: TableFilterOption<T>[];
  selectedFilters: TableFilter[];
  addFilter: (filter: TableFilterOption<T>) => void;
  changeFilter: (name: string, value: string) => void;
  removeFilter: (name: string) => void;
  clearFilters: () => void;
  children?: React.ReactNode;
  // TODO: Filters should be optional instead of this flag
  hideFilters?: boolean;
  reverseSearchAndFilters?: boolean;
  reverseFiltersAndClearButton?: boolean;
  endSlot?: React.ReactNode;
  startSlot?: React.ReactNode;
  showSeparator?: boolean;
  hideSearch?: boolean;

  promptFilter?: React.ReactNode;
};

function FlyTableControlsInner<T extends object>({
  search,
  setSearch,
  fullSearchWidth = false,
  possibleFilters = [],
  selectedFilters,
  addFilter,
  changeFilter,
  removeFilter,
  clearFilters,
  children,
  hideFilters = false,
  hideSearch = false,

  reverseSearchAndFilters = false,

  endSlot,
  startSlot,
  showSeparator = true,
  promptFilter,

  ...rest
}: FlyTableControlsProps<T>) {
  const [filtersOpen, setFiltersOpen] = useState(!!promptFilter);
  const { t } = useTranslation();

  const filtersVisible = promptFilter
    ? true
    : !hideFilters && !!possibleFilters?.length;

  const toggleFilters = () => {
    setFiltersOpen((prev) => !prev);
  };

  const filtersLeft = useMemo(() => {
    return possibleFilters?.filter(
      (filter) =>
        !selectedFilters.some(
          (selectedFilter) => selectedFilter.key === filter.key
        )
    );
  }, [possibleFilters, selectedFilters]);

  useEffect(() => {
    if (!selectedFilters.length && !promptFilter) {
      setFiltersOpen(false);
    }
  }, [promptFilter, selectedFilters.length]);
  // Do not auto-close when selectedFilters becomes empty: URL-derived filters can
  // briefly be empty when possibleFilters don't yet match (e.g. author filter
  // before authors load), which would close the panel after applying author/last run.
  // Panel closes only on explicit Clear or filter icon toggle.

  if (hideSearch) {
    return null;
  }

  const addFilterButton =
    selectedFilters.length > 0 || promptFilter ? (
      <IconButton
        color='neutral'
        variant='ghost'
        onClick={toggleFilters}
        icon='table_filter_notification'
        data-testid='table-filter-panel-toggle'
      />
    ) : (
      <FlyFilterDropdown
        possibleFilters={filtersLeft ?? []}
        onChange={(filter) => {
          addFilter(filter);
          setFiltersOpen(true);
        }}
        trigger={
          <IconButton
            color='neutral'
            variant='ghost'
            icon='table_filter'
            data-testid='table-filter-add'
          />
        }
      />
    );

  return (
    <div className='min-w-0 mb-fly-5 flex flex-col' {...rest}>
      <div className={cn('flex justify-between gap-fly-5 py-fly-4')}>
        {!!children && (
          <div className='flex flex-shrink-0 items-center gap-fly-3'>
            {children}
          </div>
        )}
        <div
          className={cn(
            'flex flex-grow-1 items-center justify-end gap-fly-3',
            reverseSearchAndFilters ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          {startSlot}
          <TextField
            data-testid='table-search'
            name='search'
            placeholder={t('app.common.actions.search', {
              defaultValue: 'Search...',
            })}
            data-fullstory-unmask
            className={classNames(
              'flex-grow-1',
              fullSearchWidth ? 'w-full' : 'max-w-[246px]'
            )}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            iconStart='search'
            iconEnd={
              search ? (
                <FlySvgSprite
                  onClick={() => setSearch('')}
                  name='cross_2'
                  size={14}
                  className='mr-fly-2 cursor-pointer'
                  role='button'
                  aria-label={t('app.common.actions.clear_search', {
                    defaultValue: 'Clear search',
                  })}
                />
              ) : undefined
            }
          />
          {endSlot}
          {filtersVisible && addFilterButton}
        </div>
      </div>
      {showSeparator && <Separator size='4' />}
      {!filtersVisible && promptFilter && (
        <div className='min-w-0 mt-fly-5 flex max-w-full flex-wrap items-center gap-fly-2'>
          {promptFilter}
        </div>
      )}
      {filtersOpen && filtersVisible && (
        <div
          className='min-w-0 mt-fly-5 flex flex-wrap items-center gap-fly-2'
          data-testid='table-filters-row'
        >
          {promptFilter && (
            <div className='min-w-0 flex max-w-full shrink-0'>
              {promptFilter}
            </div>
          )}
          {selectedFilters.map((filter) => (
            <div key={filter.key} className='min-w-0 max-w-full shrink-0'>
              <FlyFilterDetailsDropdown
                filterOption={
                  possibleFilters.find((f) => f.key === filter.key)!
                }
                filter={filter}
                onChange={(value) => changeFilter(filter.key, value)}
                onRemove={() => removeFilter(filter.key)}
              />
            </div>
          ))}
          {!!filtersLeft?.length && filtersLeft.length > 0 && (
            <div className='flex flex-1 shrink-0'>
              <FlyFilterDropdown
                possibleFilters={filtersLeft ?? []}
                onChange={addFilter}
                trigger={
                  <Button
                    size='1'
                    variant='ghost'
                    color='neutral'
                    iconStart='plus'
                    data-testid='table-filter-inline-add'
                  >
                    {t('app.dashboard.table_actions.add_filter', {
                      defaultValue: 'Add Filter',
                    })}
                  </Button>
                }
              />
            </div>
          )}
          <Button
            size='1'
            variant='ghost'
            className='shrink-0'
            data-testid='table-filter-clear-all'
            onClick={() => {
              clearFilters();
              setFiltersOpen(false);
            }}
          >
            {t('app.dashboard.table_actions.clear_filter', {
              defaultValue: 'Clear Filter',
            })}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Memoized so filter bar does not re-render when only table data/loading changes (e.g. after filter refetch). */
export const FlyTableControls = React.memo(
  FlyTableControlsInner
) as typeof FlyTableControlsInner;
